import { useCallback, useMemo } from "react";
import type { CustomerFilterInput } from "@/gql/graphql";
import { useAuthStore } from "@/lib/store/auth-enhanced";
import { useSalonStore } from "@/lib/store/salon";
import { getUserFriendlyErrorMessage } from "../utils/customer-utils";
import { useCreateCustomer, useDeleteCustomer, useUpdateCustomer } from "./use-customer-mutation";
import { useCustomerQuery } from "./use-customer-query";

interface UseCustomersProps {
  filters?: CustomerFilterInput;
  salonId?: string | null;
  skip?: boolean;
}

export const useCustomers = ({ filters, salonId, skip = false }: UseCustomersProps = {}) => {
  const { currentSalon } = useSalonStore();
  const { user } = useAuthStore();

  const finalSalonId = salonId !== undefined ? salonId : currentSalon?.id || null;

  const {
    data: rawCustomers,
    pagination,
    loading,
    error,
    refetch,
    loadMore,
    hasMore,
  } = useCustomerQuery({
    salonId: finalSalonId,
    filters,
    skip: finalSalonId === null || !user?.id || skip,
  });

  const { execute: createCustomer, isLoading: creating } = useCreateCustomer();
  const { execute: updateCustomer, isLoading: updating } = useUpdateCustomer();
  const { execute: deleteCustomer, isLoading: deleting } = useDeleteCustomer();

  // Return customers data directly from GraphQL
  const customers = rawCustomers;

  const errorMessage = useMemo(() => {
    if (!error) return null;
    return getUserFriendlyErrorMessage(error);
  }, [error]);

  const handleCreateCustomer = useCallback(
    async (input: any) => {
      if (!finalSalonId) throw new Error("No salon selected");
      const customer = await createCustomer(finalSalonId, input);
      refetch();
      return customer;
    },
    [createCustomer, finalSalonId, refetch]
  );

  const handleUpdateCustomer = useCallback(
    async (customerId: string, input: any) => {
      if (!finalSalonId) throw new Error("No salon selected");
      const customer = await updateCustomer(finalSalonId, customerId, input);
      refetch();
      return customer;
    },
    [updateCustomer, finalSalonId, refetch]
  );

  const handleDeleteCustomer = useCallback(
    async (customerId: string) => {
      if (!finalSalonId) throw new Error("No salon selected");
      await deleteCustomer(finalSalonId, customerId);
      refetch();
    },
    [deleteCustomer, finalSalonId, refetch]
  );

  return {
    customers,
    pagination,
    loading, // Only query loading, not mutation loading
    error,
    errorMessage,
    refetch,
    loadMore,
    hasMore,

    // Actions
    createCustomer: handleCreateCustomer,
    updateCustomer: handleUpdateCustomer,
    deleteCustomer: handleDeleteCustomer,

    // Loading states
    isCreating: creating,
    isUpdating: updating,
    isDeleting: deleting,
  };
};
